import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, of } from 'rxjs';
import { environment } from 'src/environments/environment';

export type AudienceType = 'company' | 'individual';
export type DemoIntent =
  | 'exploring'
  | 'planning'
  | 'hiring-now'
  | 'job-seeker';

const HUBSPOT_MEETING_URL = 'https://meetings-eu1.hubspot.com/faisal-alshareef';
const JOB_SEEKER_URL =
  'https://app.uptal.com/interview/apply/MKzGqLorRZlxq4EbdVe24N8AQ';

@Component({
  selector: 'app-book-demo-dialog',
  templateUrl: './book-demo-dialog.component.html',
  styleUrls: ['./book-demo-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookDemoDialogComponent {
  @ViewChild('stepForm') stepForm?: ElementRef<HTMLFormElement>;

  step: 1 | 2 | 3 = 1;

  private readonly step1Fields: Array<keyof typeof this.form.controls> = [
    'intent',
    'expectedHires',
  ];
  private readonly step2Fields: Array<keyof typeof this.form.controls> = [
    'audience',
    'firstName',
    'lastName',
    'companyName',
    'email',
    'phone',
  ];

  intents: { value: DemoIntent; titleKey: string; descKey: string }[] = [
    {
      value: 'hiring-now',
      titleKey: 'bookDemoDialog.intents.hiringNowTitle',
      descKey: 'bookDemoDialog.intents.hiringNowDesc',
    },
    {
      value: 'planning',
      titleKey: 'bookDemoDialog.intents.planningTitle',
      descKey: 'bookDemoDialog.intents.planningDesc',
    },
    {
      value: 'exploring',
      titleKey: 'bookDemoDialog.intents.exploringTitle',
      descKey: 'bookDemoDialog.intents.exploringDesc',
    },
    {
      value: 'job-seeker',
      titleKey: 'bookDemoDialog.intents.jobSeekerTitle',
      descKey: 'bookDemoDialog.intents.jobSeekerDesc',
    },
  ];

  hireRanges = ['1–10', '11–50', '51–200', '200+'];

  form = this.fb.nonNullable.group({
    audience: this.fb.nonNullable.control<AudienceType>('company', {
      validators: [Validators.required],
    }),
    firstName: this.fb.nonNullable.control('', {
      validators: [Validators.required, Validators.maxLength(100)],
    }),
    lastName: this.fb.nonNullable.control('', {
      validators: [Validators.required, Validators.maxLength(100)],
    }),
    companyName: this.fb.nonNullable.control('', {
      validators: [Validators.required, Validators.minLength(2)],
    }),
    email: this.fb.nonNullable.control('', {
      validators: [Validators.required, Validators.email],
    }),
    phone: this.fb.nonNullable.control('', {
      validators: [
        Validators.required,
        Validators.pattern(/^[+\d][\d\s\-()]{5,19}$/),
      ],
    }),
    intent: this.fb.nonNullable.control<DemoIntent | ''>('', {
      validators: [Validators.required],
    }),
    expectedHires: this.fb.nonNullable.control(''),
  });

  submitting = false;
  hubspotLoading = true;
  hubspotUrl?: SafeResourceUrl;
  private leadSaved = false;
  private demoScheduledTracked = false;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<BookDemoDialogComponent>,
    private translate: TranslateService,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.form.controls.audience.valueChanges.subscribe((v) => {
      const nameCtrl = this.form.controls.companyName;
      if (v === 'company') {
        nameCtrl.setValidators([Validators.required, Validators.minLength(2)]);
      } else {
        nameCtrl.clearValidators();
        nameCtrl.setValue('');
      }
      nameCtrl.updateValueAndValidity({ emitEvent: false });
    });

    this.form.controls.intent.valueChanges.subscribe((v) => {
      const hiresCtrl = this.form.controls.expectedHires;
      if (v === 'planning' || v === 'hiring-now') {
        hiresCtrl.setValidators([Validators.required]);
      } else {
        hiresCtrl.clearValidators();
        hiresCtrl.setValue('');
      }
      hiresCtrl.updateValueAndValidity({ emitEvent: false });
    });
  }

  onHubspotLoaded(): void {
    this.hubspotLoading = false;
    this.cdr.markForCheck();
  }

  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.demoScheduledTracked) return;
    if (!event?.origin?.includes('hubspot.com')) return;
    const data = event.data;
    if (!data || typeof data !== 'object') return;
    if (data.meetingBookSucceeded !== true) return;

    this.demoScheduledTracked = true;
    this.pushGtmEvent('schedule_demo');
  }

  private pushGtmEvent(eventName: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    const { audience, companyName, intent, expectedHires } = this.form.getRawValue();
    w.dataLayer.push({
      event: eventName,
      intent,
      expected_hires:
        intent === 'hiring-now' || intent === 'planning' ? expectedHires : null,
      audience,
      company_name: audience === 'company' ? companyName : null,
    });
  }

  get showCompanyName(): boolean {
    return this.form.controls.audience.value === 'company';
  }

  get showExpectedHires(): boolean {
    const intent = this.form.controls.intent.value;
    return intent === 'planning' || intent === 'hiring-now';
  }

  get isJobSeeker(): boolean {
    return this.form.controls.intent.value === 'job-seeker';
  }

  readonly jobSeekerUrl = JOB_SEEKER_URL;

  selectAudience(value: AudienceType): void {
    this.form.controls.audience.setValue(value);
  }

  selectIntent(value: DemoIntent): void {
    this.form.controls.intent.setValue(value);
  }

  selectHireRange(value: string): void {
    this.form.controls.expectedHires.setValue(value);
  }

  goToStep2(): void {
    if (!this.validateStep(this.step1Fields)) return;
    this.step = 2;
    this.cdr.markForCheck();
  }

  goToSchedule(): void {
    if (this.submitting) return;
    if (!this.validateStep(this.step2Fields)) return;

    this.submitting = true;
    this.cdr.markForCheck();

    this.saveLead().subscribe(() => {
      this.buildHubspotUrl();
      this.hubspotLoading = true;
      this.step = 3;
      this.cdr.markForCheck();
    });
  }

  private validateStep(fields: Array<keyof typeof this.form.controls>): boolean {
    const invalid = fields.filter((f) => this.form.controls[f]?.invalid);
    if (invalid.length === 0) return true;
    invalid.forEach((f) => this.form.controls[f]?.markAsTouched());
    this.cdr.markForCheck();
    this.scrollToFirstInvalid(fields);
    return false;
  }

  private scrollToFirstInvalid(order: Array<keyof typeof this.form.controls>): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const firstInvalid = order.find((name) => this.form.controls[name]?.invalid);
    if (!firstInvalid || !this.stepForm) return;

    // Wait a tick so any *ngIf-rendered error messages are in the DOM.
    setTimeout(() => {
      const el = this.stepForm!.nativeElement.querySelector<HTMLElement>(
        `[data-field="${firstInvalid}"]`,
      );
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: true });
    }, 0);
  }

  private saveLead() {
    if (this.leadSaved) {
      return of(null).pipe(finalize(() => (this.submitting = false)));
    }
    const {
      audience,
      firstName,
      lastName,
      companyName,
      email,
      phone,
      intent,
      expectedHires,
    } = this.form.getRawValue();
    const payload = {
      audience,
      first_name: firstName,
      last_name: lastName,
      company_name: audience === 'company' ? companyName : null,
      email,
      phone,
      intent,
      expected_hires:
        intent === 'hiring-now' || intent === 'planning' ? expectedHires : null,
    };

    return this.http
      .post(`${environment.apiUrl}v1/demo-requests`, payload)
      .pipe(
        catchError(() => of(null)), // proceed to HubSpot even if our backend call fails
        finalize(() => {
          this.submitting = false;
          this.leadSaved = true;
        }),
      );
  }

  back(): void {
    if (this.step === 2) this.step = 1;
    else if (this.step === 3) this.step = 2;
    this.cdr.markForCheck();
  }

  close(): void {
    this.dialogRef.close();
  }

  private buildHubspotUrl(): void {
    const params = new URLSearchParams({ embed: 'true' });
    const {
      audience,
      firstName,
      lastName,
      companyName,
      email,
      phone,
      intent,
      expectedHires,
    } = this.form.getRawValue();
    if (firstName) params.set('firstname', firstName);
    if (lastName) params.set('lastname', lastName);
    if (email) params.set('email', email);
    if (phone) params.set('phone', phone);
    if (audience === 'company' && companyName) params.set('company', companyName);
    if (intent) {
      const key = this.intents.find((i) => i.value === intent)?.titleKey;
      const intentLabel = key ? this.translate.instant(key) : intent;
      params.set('intent', intentLabel);
    }
    if (expectedHires) params.set('expected_hires', expectedHires);

    this.hubspotUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `${HUBSPOT_MEETING_URL}?${params.toString()}`,
    );
  }
}
